/**
 * Two guards that every global keyboard or clipboard handler needs, in one place.
 *
 * They were written twice already — once in map/shortcuts.ts and once inside
 * PreviewControl — and a third handler was about to copy them again. The rules they encode
 * are not obvious and both matter: inside a text field Ctrl+Z means the text, not the
 * polygon; and the map pans with the arrow keys and handles its own shortcuts, so stealing
 * keys from it takes away map navigation.
 */

const TEXT_ENTRY = /^(input|textarea|select)$/i;

/** Focus is in something that eats keystrokes as text. */
export const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  // `=== true`, not a bare read: `isContentEditable` is typed `boolean` by lib.dom but is
  // absent on elements that never got the attribute, so the bare form returns `undefined`
  // from a function that promises a boolean. Harmless where it is used as a condition, and
  // a lie the next caller would be entitled to believe.
  return TEXT_ENTRY.test(target.tagName) || target.isContentEditable === true;
};

/**
 * Focus is on the map SURFACE. Leaflet has no canvas — the drawing is SVG inside panes — so
 * the test is the container, which is also the element Leaflet makes focusable and binds its
 * own arrow-key panning to.
 *
 * `.leaflet-control` is excluded, and that exclusion is the whole point of the second line:
 * our panels moved INTO the map's corners (map/controls.ts), so "inside `.leaflet-container`"
 * stopped meaning "on the map" the moment they did. Without it, clicking the ← button of the
 * date control would focus something inside the container and the ←/→ keys would go silent —
 * on the very control they step.
 */
export const isMapTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest(".leaflet-control") !== null) return false;
  return target.closest(".leaflet-container") !== null;
};
